import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useSelector} from "react-redux"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {useMap} from "../../../../hooks/map"
import {useFilter} from "../../../../hooks/filter"
import {SelectItem} from "../../../../components/Select"
import {LocationModel} from "../../../../types/location"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useHistory} from "react-router-dom"
import {useLocalization} from "../../../../hooks/localization"

const BACK_URL = "/settings"

export const LocationEditScreen = memo(function LocationEditScreen() {
    const {t} = useLocalization()
    const {success, failure} = useMessage()
    const [rpc] = useRPC()
    const history = useHistory()
    const config = useSelector(state => state.control.config)
    const locationList = useSelector(state => state.control.locationList)

    const countryList: SelectItem<LocationModel>[] = useMap(
        useFilter(locationList, location => location?.type === "country", []),
        location => ({
            value: location,
            label: location?.name
        }),
        []
    ).sort((a, b) => (a?.value?.name > b?.value?.name ? 1 : -1))

    const [country, setCountry] = useState<SelectItem<LocationModel>>(
        countryList.find(
            c =>
                c.value.id ===
                (config?.location_v2?.type === "country"
                    ? config?.location_v2?.id
                    : config?.location_v2?.parent_country_id)
        ) || null
    )

    const cityList: SelectItem<LocationModel>[] = useMap(
        useFilter(
            locationList,
            location => location.type === "city" && location?.parent_country_id === country?.value?.id,
            [country?.value?.id]
        ),
        location => ({
            value: location,
            label: location.name
        }),
        []
    ).sort((a, b) => (a?.value?.name > b?.value?.name ? 1 : -1))

    const [city, setCity] = useState<SelectItem<LocationModel>>(
        cityList.find(
            c =>
                c.value.parent_country_id === country.value.id &&
                c.value.id ===
                    (config.location_v2.type === "city" ? config.location_v2.id : config.location_v2.parent_country_id)
        ) || null
    )
    const districtList: SelectItem<LocationModel>[] = useMap(
        useFilter(
            locationList,
            location => location.type === "district" && location?.parent_city_id === city?.value?.id,
            [city?.value?.id]
        ),
        location => ({
            value: location,
            label: location.name
        }),
        []
    ).sort((a, b) => (a?.value?.name > b?.value?.name ? 1 : -1))
    const [district, setDistrict] = useState<SelectItem<LocationModel>>(
        config?.location_v2.type === "city"
            ? null
            : districtList.find(t => t.value.id === config?.location_v2.id) || null
    )

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const updateLocation = useCallback(() => {
        rpc.updateLocations().then(() => {
            window.location.reload()
        })
    }, [rpc])

    const isFieldsValid = useCallback((): boolean => {
        if (country?.value == null) {
            failure(t("pls_select_a_country"))
            return
        }
        if (city?.value == null) {
            failure(t("pls_select_a_city"))
            return
        }
        return true
    }, [t, city, country, failure])

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }
        const location_v2 = city?.value
        return rpc
            .updateConfigs({...config, location_v2})
            .then(() => {
                success(t("successfully_saved"))
                onBackPressed()
            })
            .catch(failure)
    }, [t, city, config, failure, isFieldsValid, onBackPressed, rpc, success])

    const buttons = useMemo(
        () => [
            {
                text: t('update'),
                className: "blue",
                onClick: updateLocation
            },
            {
                text: t("save"),
                className: "green",
                onClick: onSavePressed
            },
            {
                text: t("cancel"),
                className: "red",
                url: BACK_URL
            }
        ],
        [t, onSavePressed, updateLocation]
    )

    useEffect(() => {
        setCountry(countryList.find(t => t?.value?.id === country?.value?.id) || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryList])

    useEffect(() => {
        setCity(cityList.find(t => t.value.id === city?.value?.id) || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityList])

    useEffect(() => {
        setDistrict(districtList.find(t => t.value.parent_city_id === city?.value?.id) || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districtList])

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer className="service-edit" buttons={buttons} backUrl={BACK_URL}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingSelectComponent
                            label={t("project_informations.country")}
                            options={countryList}
                            onChange={setCountry}
                            value={country}
                        />
                        <SettingSelectComponent label={t("city")} options={cityList} onChange={setCity} value={city} />
                        <SettingSelectComponent
                            label={t("town")}
                            options={districtList}
                            onChange={setDistrict}
                            value={district}
                        />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
